import {apiFetch, handleResponse, JSON_HEADERS} from "./ApiService";

export const ERROR_MESSAGES = {
    loginFail: 'Failed to login',
    meFail: 'Failed to fetch user data',
    logoutFail: 'Failed to logout',
    resetPasswordFail: 'Failed to reset password',
    updatePasswordFail: 'Failed to update password',
};

type LoginRequest = {
    username: string;
    password: string;
    recaptchaToken?: string;
}

type ResetPasswordRequest = {
    token: string;
    password: string;
}

type UpdatePasswordRequest = {
    password: string;
    newPassword: string;
}

type ConfigResponse = {
    siteKey: string;
}

type ChangeEmailRequest = {
    newEmail: string;
};

export type UserInfo = {
    tenantId: string;
    email?: string;
}

export const login = async (request: LoginRequest): Promise<void> => {
    const options = {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(request),
    };
    const response = await apiFetch("/api/login", options);
    if (response.status !== 204) {
        if (response.status === 401) {
            throw new Error('Invalid credentials.');
        } else if (response.status === 403) {
            throw new Error('Forbidden: Invalid recaptcha token.');
        }
        handleResponse(response, ERROR_MESSAGES.loginFail);
    }
};

export const logout = async (): Promise<void> => {
    const response = await apiFetch("/api/logout", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
    });
    handleResponse(response, ERROR_MESSAGES.logoutFail);
};

export const resetPassword = async (request: ResetPasswordRequest): Promise<void> => {
    const options = {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(request),
    };
    const response = await apiFetch('/api/reset-password', options);

    if (response.status === 401) {
        throw new Error('Unauthorized: Invalid token or expired.');
    }
    handleResponse(response, ERROR_MESSAGES.resetPasswordFail);
};

export const updatePassword = async (request: UpdatePasswordRequest): Promise<void> => {
    const options = {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(request),
    };
    const response = await apiFetch('/api/set-password', options);
    if (response.status === 401) {
        throw new Error('Invalid password.');
    }
    handleResponse(response, ERROR_MESSAGES.updatePasswordFail);
}

export const me = async (): Promise<UserInfo> => {
    const response = await apiFetch("/api/me", {
        method: "GET",
        headers: JSON_HEADERS,
    });
    handleResponse(response, ERROR_MESSAGES.meFail);
    return await response.json();
};

export const changeEmail = async (request: ChangeEmailRequest): Promise<void> => {
    const options = {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(request),
    };
    const response = await apiFetch('/api/change-email', options);

    if (!response.ok) {
        let msg = 'Failed to change email';
        const ct = response.headers.get('content-type') ?? '';

        if (ct.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            msg = (data?.message ?? data?.error ?? JSON.stringify(data)).toString();
        } else {
            const text = await response.text().catch(() => '');
            msg = text?.trim() || msg;
        }
        throw new Error(msg);
    }
};

export const getConfig = async (): Promise<ConfigResponse> => {
    const response = await apiFetch("/api/configs", {
        method: "GET",
        headers: JSON_HEADERS,
    });
    handleResponse(response, ERROR_MESSAGES.meFail);
    return await response.json()
}